/**
 * Re-running Compare without leaving the modal.
 *
 * WHY THE BUTTON EXISTS. The per-card re-check answers one question about one
 * row ("did you clean up that URL?"). Nothing re-asked the WHOLE question, so a
 * recruiter who went to Thrive, fixed a profile and came back saw the old
 * verdict until they closed the modal and pressed Compare again — and the fix
 * that matters most is the one that CHANGES the answer: "a Lee Park le borro el
 * linkedin url, luego matchear con otro Lee Park, pero eso no lo veo hasta que
 * vuelvo a hacer click en compare" (operator, 2026-09-14).
 *
 * WHY A REFRESH IS NOT JUST `start()`. Almost everything the modal shows is
 * server truth re-read on every poll, so a fresh run simply corrects it. The
 * exception is the per-row answer to a W2/W3 card: Compare persists nothing,
 * those live in component state until `onContinueToExport` hands them over. The
 * operator's own framing of the state model is the reason this is the only
 * exception worth code — "el único que afecta es si quedan en to review, porque
 * el resto o se ignora, equivalente al to review, o bien se actualiza de algún
 * modo directo desde la modal" (2026-09-14). Every other card action
 * (`leaveOut`, `markDifferentPerson`, `addOneContactToSearch`,
 * `recheckDuplicateLinkedIn`, and their undos) writes through immediately.
 *
 * AND WHY CARRYING THEM OVER BLINDLY IS THE DANGEROUS DEFAULT. A decision is an
 * answer to a SPECIFIC question — this row, against this Thrive contact, in
 * this kind of conflict. Re-running can change the question while keeping the
 * row: the Lee Park above stops matching #4471 by name and starts matching
 * #6012, and an "update" answered about the first would be applied to the
 * second. The recruiter would never see the swap, because the card looks the
 * same. So a decision survives only while its question is byte-identical, and
 * the modal says how many it dropped rather than dropping them quietly.
 */

import type { CompareResult } from "@nu-assist/contracts";

/**
 * What each decided-about contact was being asked, keyed by import contact id.
 *
 * The value is an OPAQUE identity string — compare it, never parse it. It
 * folds in the three things that make two cards a different question:
 *
 *   · the conflict KIND (w1/w2/w3) — a row promoted from W3 to W2 is being
 *     asked something else entirely;
 *   · the Thrive contact it is against — the swap described above;
 *   · `names_match` — the server-derived flag that routes a W2 between the
 *     "Update for current role" section and "Needs your decision". Same row,
 *     same target, different card and different question.
 *
 * A contact absent from the result is absent from the map, which the
 * revalidation reads as "no longer in conflict".
 */
export function conflictQuestions(result: CompareResult | null): Map<string, string> {
  const out = new Map<string, string>();
  if (!result?.conflicts) return out;
  const groups: Array<[string, unknown]> = [
    ["w1", result.conflicts.w1_duplicate_linkedin],
    ["w2", result.conflicts.w2_identity_mismatch],
    ["w3", result.conflicts.w3_unverified_name_match],
  ];
  for (const [kind, clusters] of groups) {
    for (const cluster of (clusters ?? []) as Array<{
      contacts?: Array<{
        import_contact_id: string;
        contact_id_thrive?: string | null;
        names_match?: boolean | null;
      }>;
    }>) {
      for (const contact of cluster.contacts ?? []) {
        // FIRST WRITE WINS, so a row appearing in two clusters cannot make the
        // key depend on iteration order — which would clear a decision on a
        // refresh that changed nothing.
        if (out.has(contact.import_contact_id)) continue;
        out.set(
          contact.import_contact_id,
          `${kind}|${contact.contact_id_thrive ?? ""}|${contact.names_match ?? ""}`
        );
      }
    }
  }
  return out;
}

export interface RevalidatedDecisions<T> {
  /** The answers whose question survived the re-run, unchanged. */
  kept: T;
  /** Contacts whose answer was dropped, because their question changed or went
   *  away. The count is what the modal reports; the ids are for tests and for
   *  anyone who wants to highlight the affected rows later. */
  clearedIds: string[];
}

/**
 * Drop the answers whose question the re-run changed; keep the rest verbatim.
 *
 * `before` must be the questions as they stood when the answers were given —
 * snapshot it at the moment Refresh is pressed, not from whatever `result` is
 * current when the new one lands.
 *
 * An answer about a contact missing from `before` is DROPPED rather than kept.
 * It should not be possible (a card the recruiter answered was on screen, so it
 * was in the result), and if it happens the honest reading is that we do not
 * know what they were answering — which is not a licence to send it to Thrive.
 */
export function revalidateDecisions<V>(
  decisions: Record<string, V>,
  before: Map<string, string>,
  after: Map<string, string>
): RevalidatedDecisions<Record<string, V>> {
  const kept: Record<string, V> = {};
  const clearedIds: string[] = [];
  for (const [contactId, decision] of Object.entries(decisions)) {
    const was = before.get(contactId);
    if (was !== undefined && after.get(contactId) === was) {
      kept[contactId] = decision;
    } else {
      clearedIds.push(contactId);
    }
  }
  return { kept, clearedIds };
}

/** The same rule for an id SET — the company-change section stores the rows the
 *  recruiter UNCHECKED, which is an answer like any other. */
export function revalidateDecisionSet(
  ids: ReadonlySet<string>,
  before: Map<string, string>,
  after: Map<string, string>
): RevalidatedDecisions<Set<string>> {
  const asRecord: Record<string, true> = {};
  for (const id of ids) asRecord[id] = true;
  const { kept, clearedIds } = revalidateDecisions(asRecord, before, after);
  return { kept: new Set(Object.keys(kept)), clearedIds };
}

/**
 * The line the modal shows after a refresh.
 *
 * Says what happened to the recruiter's own work, in their terms. Silent when
 * nothing was dropped: a refresh that cost nothing should not open with a
 * report about decisions.
 */
export function refreshNoticeLine(clearedCount: number): string | null {
  if (clearedCount <= 0) return null;
  const rows = clearedCount === 1 ? "1 decision" : `${clearedCount} decisions`;
  const they = clearedCount === 1 ? "that contact changed" : "those contacts changed";
  return `Checked again. ${rows} cleared because ${they} in Thrive. Please answer again.`;
}
